import React, { useEffect, useState } from "react";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { MapPin, CheckCircle2, X } from "lucide-react";
import { getPosition } from "@/lib/geo";

export function GeoCapture({ open, onConfirm, onClose, mode = "in" }) {
  const [pos, setPos] = useState(null);
  const [err, setErr] = useState(null);
  const [acquiring, setAcquiring] = useState(false);

  useEffect(() => {
    if (!open) { setPos(null); setErr(null); return; }
    setAcquiring(true);
    getPosition()
      .then((p) => { setPos(p); setErr(null); })
      .catch((e) => setErr(e.message || "Unable to get location"))
      .finally(() => setAcquiring(false));
  }, [open]);

  return (
    <Drawer open={open} onOpenChange={(v) => !v && onClose()}>
      <DrawerContent
        className="border-t"
        style={{ background: "#111520", borderColor: "#1E2640" }}
      >
        <div className="mx-auto w-12 h-1.5 rounded-full mt-2 mb-4" style={{ background: "#1E2640" }} />
        <div className="px-6 pb-8" data-testid="geo-capture">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>
                Step 2 of 2
              </div>
              <div className="text-xl font-syne font-bold tracking-tight" style={{ color: "#E8ECFF" }}>
                Geo-tag check-{mode === "in" ? "in" : "out"}
              </div>
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "#161B2C", border: "1px solid #1E2640", color: "#7A85A3" }}>
              <X size={16} />
            </button>
          </div>

          <div className="relative rounded-2xl overflow-hidden h-44 mb-4" style={{ background: "#0A0D14", border: "1px solid #1E2640" }}>
            <div className="absolute inset-0 geo-grid opacity-40" />
            <div className="absolute inset-0 flex items-center justify-center">
              {pos ? (
                <div className="relative">
                  <div className="w-3 h-3 rounded-full" style={{ background: "#22D3A5", boxShadow: "0 0 0 8px rgba(34,211,165,0.2), 0 0 0 16px rgba(34,211,165,0.1)" }} />
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm" style={{ color: "#7A85A3" }}>
                  <MapPin size={18} className="animate-pulse-dot" />
                  {acquiring ? "Acquiring location…" : err || "Waiting…"}
                </div>
              )}
            </div>
          </div>

          {pos && (
            <div className="rounded-2xl p-4 mb-4 grid grid-cols-3 gap-2" style={{ background: "#161B2C", border: "1px solid #1E2640" }} data-testid="geo-coords">
              <div>
                <div className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Lat</div>
                <div className="font-mono text-sm" style={{ color: "#E8ECFF" }}>{pos.lat.toFixed(5)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>Lng</div>
                <div className="font-mono text-sm" style={{ color: "#E8ECFF" }}>{pos.lng.toFixed(5)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest" style={{ color: "#7A85A3" }}>±Acc</div>
                <div className="font-mono text-sm" style={{ color: "#E8ECFF" }}>{Math.round(pos.accuracy)}m</div>
              </div>
              <div className="col-span-3 flex items-center gap-2 text-xs font-bold uppercase tracking-widest" style={{ color: "#22D3A5" }}>
                <CheckCircle2 size={14} /> Verified
              </div>
            </div>
          )}

          {err && (
            <div className="rounded-xl px-4 py-3 mb-3 text-sm" style={{ background: "rgba(247,90,90,0.1)", border: "1px solid rgba(247,90,90,0.3)", color: "#F75A5A" }}>
              {err}. You can still confirm without geo.
            </div>
          )}

          <button
            data-testid="geo-confirm-button"
            onClick={() => onConfirm(pos)}
            disabled={acquiring}
            className="w-full h-12 rounded-xl font-bold disabled:opacity-50"
            style={{ background: mode === "in" ? "#22D3A5" : "#F75A5A", color: "#0A0D14" }}
          >
            Confirm & Record
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
